import type { ClubPlayerListItem, TeamBrief, TeamListItem } from '@volee/api-client'

import { AGE_GROUP_ORDER, TEAM_GENDER_ORDER, type PlayerPosition } from '../messages/index.ts'
import { birthYearOf } from './age.ts'
import { bySurname } from './labels.ts'
import { matchesName } from './roster-filters.ts'

// What the players list narrows and orders by, kept away from JSX. The whole season is already
// in hand when these run, so none of it asks the API.

/** The four orders the list offers, in the order the select shows them. */
export const PLAYER_SORTS = ['last_name', '-last_name', '-birth_date', 'birth_date'] as const

export type PlayerSort = (typeof PLAYER_SORTS)[number]

export const PLAYER_STATUSES = ['active', 'released', 'all'] as const

export type PlayerStatus = (typeof PLAYER_STATUSES)[number]

export const PLAYER_ACCOUNT_FILTERS = ['all', 'with_account', 'without_account'] as const

export type PlayerAccountFilter = (typeof PLAYER_ACCOUNT_FILTERS)[number]

/** «Senza squadra» in the URL and in the filter: a word, because it is no squad's id. */
export const WITHOUT_TEAM = 'none'

/** A leva by its id, or the tesserati who are in none. */
export type PlayerTeamFilter = number | typeof WITHOUT_TEAM

export interface PlayerFilters {
  readonly q: string | null
  readonly team: PlayerTeamFilter | null
  readonly position: PlayerPosition | null
  /** The year of birth: the annata. */
  readonly year: number | null
  readonly account: PlayerAccountFilter
  readonly status: PlayerStatus
}

/** Every filter at its default: «Attivi» is the one that is not «everyone», because a released
 *  tesserato is something one goes looking for. */
export const NO_PLAYER_FILTERS: PlayerFilters = {
  q: null,
  team: null,
  position: null,
  year: null,
  account: 'all',
  status: 'active',
}

/** The filters off and the search left as it is: the search has a field of its own beside the
 *  button, and is not one of the filters the bar counts. */
export function withoutFilters(filters: PlayerFilters): PlayerFilters {
  return { ...NO_PLAYER_FILTERS, q: filters.q }
}

function isReleased(player: ClubPlayerListItem): boolean {
  return player.released_on != null
}

function teamsHeldBy(player: ClubPlayerListItem): readonly TeamBrief[] {
  return player.teams ?? []
}

/** «Senza squadra» (CONTEXT.md): active, and in no rosa. */
function isWithoutTeam(player: ClubPlayerListItem): boolean {
  return !isReleased(player) && teamsHeldBy(player).length === 0
}

/** The senza squadra of the season, as the filter «Senza squadra» finds them. */
export function countWithoutTeam(players: readonly ClubPlayerListItem[]): number {
  return players.filter(isWithoutTeam).length
}

function matchesTeam(player: ClubPlayerListItem, team: PlayerTeamFilter): boolean {
  if (team === WITHOUT_TEAM) return isWithoutTeam(player)
  return teamsHeldBy(player).some((held) => held.id === team)
}

function matchesStatus(player: ClubPlayerListItem, status: PlayerStatus): boolean {
  if (status === 'all') return true
  return status === 'released' ? isReleased(player) : !isReleased(player)
}

function matchesAccount(player: ClubPlayerListItem, account: PlayerAccountFilter): boolean {
  if (account === 'all') return true
  return account === 'with_account' ? player.has_account : !player.has_account
}

/** `kept` are the tesserati a svincolo was written for, or undone, since the page opened: their
 *  row stays where it was, and what the write changed — the status, the leva — does not ask again. */
export function filterPlayers(
  players: readonly ClubPlayerListItem[],
  filters: PlayerFilters,
  kept: ReadonlySet<number> = new Set(),
): readonly ClubPlayerListItem[] {
  return players.filter((player) => {
    const stays = kept.has(player.registration_id)
    return (
      (filters.q === null || matchesName(player, filters.q)) &&
      (filters.team === null || stays || matchesTeam(player, filters.team)) &&
      (filters.position === null || player.position === filters.position) &&
      (filters.year === null || birthYearOf(player) === filters.year) &&
      matchesAccount(player, filters.account) &&
      (stays || matchesStatus(player, filters.status))
    )
  })
}

/** The società's squads, oldest category first. All of them are a leva to choose, a squad nobody
 *  is in yet included: it filters to nobody, and that is the answer. */
export function orderedSquads(squads: readonly TeamListItem[]): readonly TeamListItem[] {
  return [...squads].sort(
    (a, b) =>
      AGE_GROUP_ORDER.indexOf(a.age_group) - AGE_GROUP_ORDER.indexOf(b.age_group) ||
      b.under_age - a.under_age ||
      TEAM_GENDER_ORDER.indexOf(a.gender) - TEAM_GENDER_ORDER.indexOf(b.gender),
  )
}

/** The years of birth the tesserati have, the latest first. */
export function birthYearsOf(players: readonly ClubPlayerListItem[]): readonly number[] {
  const years = players.map(birthYearOf).filter((year) => year !== null)
  return [...new Set(years)].sort((a, b) => b - a)
}

function byName(a: ClubPlayerListItem, b: ClubPlayerListItem): number {
  return bySurname(a, b) || a.registration_id - b.registration_id
}

/** A birth date is an ISO day, so the text orders it. A player with none sorts last whichever
 *  way the dates run: «from the youngest» is not a place for them to open the list. */
function byBirthDate(direction: 1 | -1) {
  return (a: ClubPlayerListItem, b: ClubPlayerListItem): number => {
    if (a.birth_date == null || b.birth_date == null) {
      return Number(a.birth_date == null) - Number(b.birth_date == null) || byName(a, b)
    }
    return direction * a.birth_date.localeCompare(b.birth_date) || byName(a, b)
  }
}

export function sortPlayers(
  players: readonly ClubPlayerListItem[],
  sort: PlayerSort,
): readonly ClubPlayerListItem[] {
  const comparators = {
    last_name: byName,
    '-last_name': (a: ClubPlayerListItem, b: ClubPlayerListItem) => byName(b, a),
    birth_date: byBirthDate(1),
    '-birth_date': byBirthDate(-1),
  } satisfies Record<PlayerSort, (a: ClubPlayerListItem, b: ClubPlayerListItem) => number>
  return [...players].sort(comparators[sort])
}
